import {
  createSession,
  SessionContext,
  SessionCreate,
  SessionCreateBody,
  SessionDisplay,
  SessionGuests,
  SessionModel,
  SessionStayPeriod,
} from './model';
import { FetchOptions, Http } from '../../core/http';
import { SessionApi } from './session.api';
import {
  catchError,
  finalize,
  first,
  map,
  share,
  switchMap,
  shareReplay,
} from 'rxjs/operators';
import { SessionRepository } from './store';
import { Env } from '../../env';
import { Observable } from 'rxjs';
import { SessionHelperService } from './session-helper.service';
import {
  getLanguageKeyFromLocalStorage,
  getLanguageKeyFromSessionStorage,
} from '../../core';

let storedSessionId: SessionModel['sessionId'];
let fetchGetSession$: Observable<SessionModel>;
let fetchCreateSession$: Observable<SessionCreate>;

const fetchGetSession = (
  sessionId: SessionModel['sessionId'],
  options: FetchOptions = {} as FetchOptions,
) => {
  const fetchData = Http.get<any>(SessionApi.get(sessionId)).pipe(
    map((res) => createSession(res)),
  );

  if (options.preventShare) {
    return fetchData;
  }

  if (!fetchGetSession$ || sessionId !== storedSessionId) {
    fetchGetSession$ = fetchData.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    storedSessionId = sessionId;
  }

  return fetchGetSession$;
};

const getHotelIDfromLocalStorage = () => {
  const location = JSON.parse(
    localStorage.getItem('sessionSettings') || '{}',
  )?.location;
  const hotelId = location?.hotelId || location?.['hotel_id'];
  return hotelId ? Number(hotelId) : undefined;
};

const getEngineIDfromLocalStorage = () => {
  const location = JSON.parse(
    localStorage.getItem('sessionSettings') || '{}',
  )?.location;
  return location?.engineId || location?.['engine_id'];
};

const getHotelIDfromSessionStorage = () => {
  const location = JSON.parse(
    sessionStorage.getItem('sessionSettings') || '{}',
  )?.location;
  const hotelId = location?.hotelId || location?.['hotel_id'];
  return hotelId ? Number(hotelId) : undefined;
};

const getEngineIDfromSessionStorage = () => {
  const location = JSON.parse(
    sessionStorage.getItem('sessionSettings') || '{}',
  )?.location;
  return location?.engineId || location?.['engine_id'];
};

const fetchCreateSession = (
  body: Partial<SessionCreateBody> = {},
  options: FetchOptions = {} as FetchOptions,
) => {
  const storageSessionSettings = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? JSON.parse(sessionStorage.getItem('sessionSettings') || '{}')
    : JSON.parse(localStorage.getItem('sessionSettings') || '{}');
  const engineIdStorage = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? getEngineIDfromSessionStorage()
    : getEngineIDfromLocalStorage();
  const hotelIdStorage = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? getHotelIDfromSessionStorage()
    : getHotelIDfromLocalStorage();
  const langStorage = window.QW_HOTEL_ENV.USE_SESSION_STORAGE
    ? getLanguageKeyFromSessionStorage()
    : getLanguageKeyFromLocalStorage();

  const stayPeriod =
    (body.context && body.context.stayPeriod) ||
    storageSessionSettings?.stayPeriod;
  const guests =
    (body.context && body.context.guests) || storageSessionSettings?.guests;
  const display = body.display || ({} as SessionDisplay);
  const sessionBody = {
    engineId: body.engineId || engineIdStorage || Env.EngineId,
    isMonoHotel: Env.IsMonoHotel,
    display: {
      culture: langStorage || display.culture || Env.DefaultLanguage,
      currency: display.currency || Env.Currency,
      countryCode: display.countryCode || Env.Country,
    },
    context: {
      stayPeriod: stayPeriod || {
        arrivalDate: Env.DefaultArrivalDate,
        departureDate: Env.DefaultDepartureDate,
      },
      guests: guests || {
        adults: Env.DefaultNumberOfAdults,
        children: Env.DefaultNumberOfChildren,
        infants: Env.DefaultNumberOfInfants,
      },
      selectedHotelId: Number(
        body.context?.selectedHotelId || hotelIdStorage || Env.HotelId,
      ),
      promoCode: body.context?.promoCode,
    },
    metadata: Env.SessionMetadata,
  } as SessionCreateBody;
  const fetchData = Http.post<SessionCreate, SessionCreateBody>(
    SessionApi.create(),
    sessionBody,
  );

  if (options.preventShare) {
    return fetchData;
  }

  if (!fetchCreateSession$) {
    fetchCreateSession$ = fetchData.pipe(share());
  }

  return fetchCreateSession$;
};

const fetchSession = (
  sessionId: SessionModel['sessionId'],
  options?: FetchOptions,
) => {
  return fetchGetSession(sessionId, options).pipe(
    map((session) => {
      SessionHelperService.storeSession(session);
      return session;
    }),
    catchError(shouldBeSessionError),
  );
};

const deleteSession = (sessionId: SessionModel['sessionId']) => {
  return Http.delete<string>(SessionApi.delete(sessionId));
};

const getSession = () => {
  if (SessionRepository.store.getValue().sessionId) {
    return SessionRepository.session$;
  }

  SessionHelperService.setLoadingInSession(true);

  const paramsSessionId = SessionHelperService.getSessionIdFromParams();
  if (paramsSessionId) {
    return SessionHelperService.fetchSessionAndHandleInvalidError(
      paramsSessionId,
    );
  }

  if (window.QW_HOTEL_ENV.USE_SESSION_STORAGE) {
    const sessionStorageSessionId =
      SessionHelperService.getSessionFromSessionStorage();
    if (sessionStorageSessionId) {
      return SessionHelperService.fetchSessionAndHandleInvalidError(
        sessionStorageSessionId,
      );
    }
  } else {
    const localStorageSessionId =
      SessionHelperService.getSessionFromLocalStorage();
    if (localStorageSessionId) {
      return SessionHelperService.fetchSessionAndHandleInvalidError(
        localStorageSessionId,
      );
    }
  }

  return SessionHelperService.createNewSession();
};

const updateContextSession = (body: Partial<SessionContext>) => {
  const session = SessionRepository.store.getValue();

  SessionHelperService.setLoadingInSession(true);

  return SessionRepository.sessionHasRooms$.pipe(
    switchMap((hasRooms) => {
      return hasRooms
        ? SessionHelperService.fetchUpdateSessionContextWithNotEmptyBasket(
            { ...session.context, ...body },
            session,
          )
        : SessionHelperService.fetchUpdateSessionContext(session.sessionId, {
            ...session.context,
            ...body,
          });
    }),
    first(),
    switchMap(() => fetchSession(session.sessionId)),
    catchError(shouldBeSessionError),
    finalize(() => SessionHelperService.setLoadingInSession(false)),
  );
};

const updateDisplaySession = (body: SessionDisplay) => {
  const session = SessionRepository.store.getValue();

  SessionHelperService.setLoadingInSession(true);

  return SessionHelperService.fetchUpdateSessionDisplay(
    session.sessionId,
    body,
  ).pipe(
    first(),
    switchMap(() => fetchSession(session.sessionId, { preventShare: true })),
    catchError(shouldBeSessionError),
    finalize(() => SessionHelperService.setLoadingInSession(false)),
  );
};

const shouldBeSessionError = (err: any) => {
  if (SessionHelperService.isInvalidSession(err)) {
    console.log('CREATE NEW SESSION');
    SessionHelperService.createNewSession().subscribe();
  }
  return Http.error(err);
};

export function updateSessionData(
  session: { context: Partial<SessionContext> },
  guests: SessionGuests,
  stayPeriod: SessionStayPeriod,
  promoCode?: string,
) {
  return updateContextSession({
    ...session.context,
    guests: guests,
    stayPeriod: stayPeriod,
    promoCode: promoCode,
  }).toPromise();
}

export function createSessionData(sessionBody: Partial<SessionCreateBody>) {
  return SessionHelperService.createNewSession(sessionBody).toPromise();
}

window.QW_HOTEL_UPDATE_SESSION_DATA = updateSessionData;
window.QW_HOTEL_CREATE_SESSION_DATA = createSessionData;

export const SessionService = {
  getSession,
  updateContextSession,
  updateDisplaySession,
  fetchSession,
  fetchGetSession,
  fetchCreateSession,
  shouldBeSessionError,
  deleteSession,
};
